/**
 * Settings → Security: how a signed-in session reads in the list ("Chrome on macOS", "Bundle for Mac — Ana's
 * MacBook Pro", "Bundle on iPhone") from what the server stores (Better Auth `userAgent`, the desktop `deviceName`),
 * and the list order (this device first, then most recently active). Pure; no IP geolocation (none is stored).
 */
import type { AccountSession } from "@bundle/contracts";

export type SessionKind = "desktop" | "mobile" | "browser";

export type SessionDescription = {
  kind: SessionKind;
  /** The desktop app's own label, when it sent one. */
  deviceName?: string;
  /** "Chrome", "Safari", "Firefox", "Edge", "Opera", "Samsung Internet"; absent when unknown or an app. */
  browser?: string;
  /** "macOS", "Windows", "iOS", "iPadOS", "Android", "Linux", "ChromeOS"; absent when unknown. */
  os?: string;
  /** The native apps (iOS/Android): their HTTP clients send `Bundle/<version>` or okhttp/CFNetwork user agents. */
  app: boolean;
};

const OS_RULES: [RegExp, string][] = [
  [/iPad/, "iPadOS"],
  [/iPhone|iPod|\biOS\b|CFNetwork|Darwin.*Mobile/, "iOS"],
  [/Android|okhttp/i, "Android"],
  [/CrOS/, "ChromeOS"],
  [/Windows/, "Windows"],
  [/Mac OS X|Macintosh|macOS/, "macOS"],
  [/Linux|X11/, "Linux"],
];

// Order matters: Edge and Opera also say "Chrome"; Chrome also says "Safari".
const BROWSER_RULES: [RegExp, string][] = [
  [/Edg(e|A|iOS)?\//, "Edge"],
  [/OPR\/|Opera/, "Opera"],
  [/SamsungBrowser\//, "Samsung Internet"],
  [/Firefox\/|FxiOS\//, "Firefox"],
  [/Chrome\/|CriOS\//, "Chrome"],
  [/Version\/[\d.]+.*Safari\//, "Safari"],
];

function first(rules: [RegExp, string][], ua: string): string | undefined {
  return rules.find(([re]) => re.test(ua))?.[1];
}

/** What a session's device, OS and browser are, as far as its user agent and device name say. */
export function describeSession(s: Pick<AccountSession, "userAgent" | "deviceName">): SessionDescription {
  const ua = s.userAgent ?? "";
  const os = first(OS_RULES, ua);
  const app = /^Bundle\b|okhttp|CFNetwork|Dalvik/i.test(ua) && !/Mozilla\//.test(ua);
  const tauri = /Tauri|Bundle-Desktop/i.test(ua);
  const out: SessionDescription = { kind: "browser", app };
  if (s.deviceName !== undefined && s.deviceName !== "") {
    out.kind = "desktop";
    out.deviceName = s.deviceName;
  } else if (tauri) {
    out.kind = "desktop";
  } else if (app || os === "iOS" || os === "iPadOS" || os === "Android") {
    out.kind = "mobile";
  }
  if (os !== undefined) out.os = os;
  if (!app && !tauri) {
    const browser = first(BROWSER_RULES, ua);
    if (browser !== undefined) out.browser = browser;
  }
  return out;
}

/** This device first, then most recently active. */
export function sortSessions<T extends Pick<AccountSession, "current" | "updatedAt">>(list: readonly T[]): T[] {
  return list.toSorted((a, b) => (a.current !== b.current ? (a.current ? -1 : 1) : b.updatedAt - a.updatedAt));
}
